import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import type { SisBundleSummary, UploadSummary } from "@canvas-sync/shared";
import { SisBundleStatusBadge } from "./SisBundleStatusBadge";
import { UploadStatusBadge } from "./UploadStatusBadge";
import {
  formatDuration,
  formatIdLabel,
  formatIdShortTime,
  formatRelative,
  groupByDate,
} from "../lib/format";
import { uploadTriggerLabel } from "../lib/labels";
import { submissionDate, type SubmissionItem } from "../lib/submissions";

function bundleTriggerLabel(b: SisBundleSummary): string {
  return b.triggeredBy === "manual-retrigger"
    ? "Manual re-trigger"
    : "Scheduled (n8n)";
}

/**
 * Canvas-submission history rendered as a collapsible timeline of date
 * sections. Mirrors `RunsTimeline` so the list pages stay visually
 * consistent: shared `<thead>`, one `<tbody>` per date bucket,
 * click-to-collapse header row, and the same caret + title + sublabel +
 * count layout.
 *
 * Bucketing uses `submittedAt` when present (terminal & most non-terminal
 * rows), falling back to `queuedAt` (uploads still SUBMITTING) or
 * `createdAt` (bundles).
 */
export function UploadsTimeline({ items }: { items: SubmissionItem[] }) {
  const groups = useMemo(() => groupByDate(items, submissionDate), [items]);
  // Default: only the newest (first) group expanded — older days collapsed.
  // Lazy initializer computes from the initial `items` prop to avoid a
  // flash of all-expanded on first paint. User toggles persist for the visit.
  const [collapsed, setCollapsed] = useState<Set<string>>(() => {
    const initialGroups = groupByDate(items, submissionDate);
    return new Set(initialGroups.slice(1).map((g) => g.key));
  });

  function toggle(key: string) {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  return (
    <table className="runs-table runs-table-grouped">
      <thead>
        <tr>
          <th>Submission</th>
          <th>Status</th>
          <th>Started by</th>
          <th>Canvas</th>
          <th>Canvas import ID</th>
          <th>Submitted</th>
          <th>Duration</th>
          <th>Rows</th>
          <th>Sync</th>
        </tr>
      </thead>
      {groups.map((g) => {
        const isCollapsed = collapsed.has(g.key);
        return (
          <tbody key={g.key} className="runs-group">
            <tr className="runs-group-head">
              <td colSpan={9}>
                <button
                  type="button"
                  className="runs-group-toggle"
                  aria-expanded={!isCollapsed}
                  onClick={() => toggle(g.key)}
                >
                  <span className="runs-group-caret" aria-hidden="true" />
                  <span className="runs-group-title">{g.label}</span>
                  {g.sublabel && (
                    <span className="runs-group-sublabel">{g.sublabel}</span>
                  )}
                  <span className="runs-group-count">
                    {g.items.length} submission
                    {g.items.length === 1 ? "" : "s"}
                  </span>
                </button>
              </td>
            </tr>
            {!isCollapsed &&
              g.items.map((item) =>
                item.kind === "upload" ? (
                  <UploadRow key={item.upload.uploadId} u={item.upload} />
                ) : (
                  <BundleRow key={item.bundle.bundleId} b={item.bundle} />
                ),
              )}
          </tbody>
        );
      })}
    </table>
  );
}

function UploadRow({ u }: { u: UploadSummary }) {
  return (
    <tr>
      <td>
        <Link
          to={`/uploads/${u.uploadId}`}
          className="run-link"
          title={u.uploadId}
        >
          {formatIdLabel(u.uploadId)}
        </Link>
      </td>
      <td>
        <UploadStatusBadge status={u.status} />
      </td>
      <td>
        <span className="trigger-pill">{uploadTriggerLabel(u.trigger)}</span>
      </td>
      <td>
        <span className={`env-tag env-tag-${u.canvasEnv}`}>{u.canvasEnv}</span>
      </td>
      <td className="mono">{u.sisImportId ?? "—"}</td>
      <td className="muted">{formatRelative(u.submittedAt ?? u.queuedAt)}</td>
      <td className="mono">{formatDuration(u.durationMs)}</td>
      <td className="mono">{u.rowsSubmitted}</td>
      <td>
        {u.relatedRunId ? (
          <Link
            to={`/syncs/${u.relatedRunId}`}
            className="muted-link small"
            title={u.relatedRunId}
          >
            {formatIdShortTime(u.relatedRunId)}
          </Link>
        ) : (
          <span className="muted">—</span>
        )}
      </td>
    </tr>
  );
}

/**
 * A SIS bundle that actually POSTed to Canvas, rendered as a sibling of
 * the standalone uploads. The "via SIS bundle" pill + link to the bundle
 * detail page distinguish the path; the rest of the columns line up.
 */
function BundleRow({ b }: { b: SisBundleSummary }) {
  return (
    <tr>
      <td>
        <Link
          to={`/sis-bundles/${encodeURIComponent(b.bundleId)}`}
          className="run-link"
          title={b.bundleId}
        >
          {formatIdLabel(b.dropId)}
        </Link>{" "}
        <span
          className="trigger-pill"
          title="Sent by the SIS bundle pipeline — see the bundle for the full submission record"
        >
          via SIS bundle
        </span>
      </td>
      <td>
        <SisBundleStatusBadge status={b.status} />
      </td>
      <td>
        <span className="trigger-pill">{bundleTriggerLabel(b)}</span>
      </td>
      <td>
        <span className={`env-tag env-tag-${b.env}`}>{b.env}</span>
      </td>
      <td className="mono">{b.sisImportId ?? "—"}</td>
      <td className="muted">{formatRelative(b.submittedAt ?? b.createdAt)}</td>
      <td className="mono">—</td>
      <td className="mono">{b.totalRowsSubmitted ?? "—"}</td>
      <td>
        {b.ghostDetectionRunId ? (
          <Link
            to={`/syncs/${b.ghostDetectionRunId}`}
            className="muted-link small"
            title={b.ghostDetectionRunId}
          >
            {formatIdShortTime(b.ghostDetectionRunId)}
          </Link>
        ) : (
          <span className="muted">—</span>
        )}
      </td>
    </tr>
  );
}
